import { BaseModel, BaseModelProps, EmptyRenderer } from '@mail/3promo';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import { Rubles } from 'Cloud/Application/Rubles';
import { xray } from 'lib/xray';
import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import { advExpId } from 'reactApp/appHelpers/featuresHelpers';
import {
    ADV_AUCFULL_3PROMO_ID,
    advAucfullEnabled,
    advAucfullMaxRenderTime,
    advAucfullMaxTestDelay,
    advAucfullMobileAllowed,
    advAucfullPerfLogEnabled,
    advAucfullShowInterval,
    DOM_ID_ADV_AUC_FULLSCREEN,
} from 'reactApp/appHelpers/featuresHelpers/features/advAucFullscreen';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import {
    isPromoLandingPage,
    isPromoQuotaTariffs,
    isQuotaPageWithoutPromos,
    isReactLandingPage,
    isSubscriptionPage,
} from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { store } from 'reactApp/store';

let lastInsertTime = 0;
let fetchPromise: Promise<boolean> | null = null;
let lastReturnTabTime = 0;
let showCountPerSession = 0;
let tabOpenOrReturnConditionCompleted = true;

let isTimeToFirstTestLogged = false;

function shouldShowAdvFullscreen(): boolean {
    if (!advAucfullEnabled) {
        return false;
    }

    // Показываем фулскрин только в начале сессии и по возвращении во вкладку
    if (!tabOpenOrReturnConditionCompleted) {
        return false;
    }

    // Проверка можно ли показать на мобильных девайсах
    if (EnvironmentSelectors.isMobile() && !advAucfullMobileAllowed) {
        return false;
    }

    const state = store.getState();
    const isViewer = ViewerSelectors.isViewerActive(state) || ViewerSelectors.isViewerOpening(state);
    const isLanding = isReactLandingPage() || isPromoLandingPage();
    const isAcceptanceLA = SettingsSelectors.getQueryParams(state).isAcceptanceLA;
    const isNewbie = UserSelectors.isNewbie(state);

    // Не показываем: в iframe, лендингах, страницах оплаты и тарифов, если не принято ЛА или это первый заход в Облако после реги и АТ
    if (
        IS_AUTO_TEST_HIDE ||
        isNewbie ||
        isAcceptanceLA ||
        EnvironmentSelectors.isIFrame() ||
        EnvironmentSelectors.isWebview() ||
        isSubscriptionPage() ||
        isPromoQuotaTariffs() ||
        isQuotaPageWithoutPromos() ||
        isLanding ||
        isViewer
    ) {
        return false;
    }

    const timeLastOpenOrReturnToTab = lastReturnTabTime || window.appReadyTime || 0;
    // Чтобы не разрывать путь юзера показываем фулскрин в некотором интервале после
    // открытия/возвращения во вкладку
    if (Date.now() - timeLastOpenOrReturnToTab > advAucfullMaxTestDelay) {
        return false;
    }

    return true;
}

/**
 * Перевод времени загрузки в словесный вид. (Украдено из octavius и @mail/adman)
 * @param duration - интервал времени в мс
 * @returns описание скорости загрузки
 */
function selectDuration(duration: number) {
    let durationType = 'fail';
    if (duration < 100) {
        durationType = 'instantly1';
    } else if (duration < 300) {
        durationType = 'instantly2';
    } else if (duration < 500) {
        durationType = 'instantly3';
    } else if (duration < 1000) {
        durationType = 'very-fast';
    } else if (duration < 2000) {
        durationType = 'fast';
    } else if (duration < 4000) {
        durationType = 'moderate';
    } else if (duration < 8000) {
        durationType = 'slightly-slow';
    } else if (duration < 15000) {
        durationType = 'slow';
    } else if (duration < 30000) {
        durationType = 'very-slow';
    }
    return durationType;
}

function logPerformance(metric: string, duration: number) {
    if (!advAucfullPerfLogEnabled) {
        return;
    }

    const durationType = selectDuration(duration);

    // fsadv-perf = Fullscreen advertisement performance
    xray.send('fsadv-perf', {
        i: {
            [`${metric}`]: duration,
            [`${metric}_e-${advExpId}`]: duration,
            [`${metric}_d-${durationType}`]: duration,
            [`${metric}_d-${durationType}_e-${advExpId}`]: duration,
        },
        dwh: {
            [metric]: duration,
        },
    });
}

if (advAucfullEnabled) {
    window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            lastReturnTabTime = Date.now();
            tabOpenOrReturnConditionCompleted = true;
        }
    });
}

class AdvAucFullscreenModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        // TODO проверить что навешивание visability: hidden из 3промо не сломает отображение
        return document.querySelector(`#${DOM_ID_ADV_AUC_FULLSCREEN}`);
    }

    isVisible(): boolean {
        const container = document.querySelector(`#${DOM_ID_ADV_AUC_FULLSCREEN}`);
        const centerEl = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);

        // Пока что колбэки в фулскрине не сделали
        // так что это единственный способ чекнуть показывается ли фулскрин

        // Условие 1 - фулскрин открыт
        const isVisible = container && centerEl ? container.contains(centerEl) : false;

        // Без этого будет баг - второй фулскирн может покаться сразу после первого,
        // в случае если юзер увидел первый и ушел в АФК на время более showlimit не закрывая его.
        // В идеале updateTime() надо обновлять на закрытие фулскрина(onHide() или onSnooze()),
        // но на данный момент мы этого делать не умеем (события просто не вызовутся при закртии фулскрина).
        // Сделаем, когда научимся детектить закрытие
        if (isVisible) {
            this.store.updateTime();
        }

        // Условие 2 - фулскрин недавно вставили в Dom и мб он догрузит что-то и откроется
        const maybeVisible = Date.now() - lastInsertTime < advAucfullMaxRenderTime;

        return isVisible || maybeVisible;
    }

    // Не несет смысла, тк промка просто лочит очередь
    // Если использовать дефолтный, то радар redraw будет спамить
    redraw(): void {}
}

export const advAucFullscreenCreator: ModelCreator = ({ models, stores }) =>
    new AdvAucFullscreenModel(
        ADV_AUCFULL_3PROMO_ID,
        models.base,
        new BaseModelProps(ADV_AUCFULL_3PROMO_ID, {
            store: new LocalStorageStore(ADV_AUCFULL_3PROMO_ID, stores.localStorage),
            renderer: new EmptyRenderer(ADV_AUCFULL_3PROMO_ID), // Нужно просто залочить очередь, рендер на строне РБ
            description: {
                taskId: 'CLOUDWEB-19446',
            },
            ignoreTestOnRedraw: false,
            hideOnRedraw: false,
            ignorePromoShowInterval: true,
            ignoreTimeWhenShowed: false,
            markAsShowed: true,
            showLimit: Infinity,
            closeLimit: Infinity,
            showInterval: advAucfullShowInterval,
            templateData: {},
            test: async (resolve, reject, item) => {
                if (!isTimeToFirstTestLogged && window.appReadyTime) {
                    isTimeToFirstTestLogged = true;
                    const timeToFirstTest = Date.now() - window.appReadyTime;
                    logPerformance('ttft', timeToFirstTest);
                }

                // Проверка на случай если новый test() будет раньше, чем закончится прошлый
                await fetchPromise;

                // Если уже показываем фулскрин - лочим очередь
                if (item.isVisible()) {
                    // Сбрасываем флаг на случай, если ушли с вкладки при открытом фулскрине
                    // иначе сразу за первым может показаться второй
                    tabOpenOrReturnConditionCompleted = false;
                    resolve();
                    return;
                }

                // Проверка остальных условий показа
                if (!shouldShowAdvFullscreen()) {
                    reject();
                    return;
                }

                // Запрашиваем и вставляем фулскрин в dom
                fetchPromise = Rubles.updateAucFullscreen() as unknown as Promise<boolean>;
                xray.send('fsadv', { i: 'request' });
                fetchPromise
                    .then((inserted: boolean) => {
                        if (inserted) {
                            lastInsertTime = Date.now();
                            showCountPerSession += 1;
                            tabOpenOrReturnConditionCompleted = false;

                            xray.send('fsadv', {
                                i: [
                                    'insert',
                                    // Ограничим сверху для наглядности графиков в графане
                                    `insert_per_session_${showCountPerSession > 30 ? 30 : showCountPerSession}`,
                                ],
                                dwh: {
                                    cnt_per_session: showCountPerSession,
                                },
                            });

                            const timeLastOpenOrReturnToTab = lastReturnTabTime || window.appReadyTime;
                            if (timeLastOpenOrReturnToTab) {
                                const timeToInsert = Date.now() - timeLastOpenOrReturnToTab;

                                logPerformance('tti', timeToInsert); // tti - time to insert
                            }

                            resolve();
                        } else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
            },
        })
    );
